import { PaymentMethodsResponseObject } from '@adyen/adyen-web/dist/types/core/ProcessResponse/PaymentMethodsResponse/types'
import {
  PaymentMethodOptions,
  PaymentMethods,
} from '@adyen/adyen-web/dist/types/types'

import { request } from './request'

export type PaymentMethodsResponseWithConfiguration = PaymentMethodsResponseObject & {
  paymentMethodsConfiguration?: {
    [key in keyof PaymentMethods]?: Partial<PaymentMethodOptions<key>>
  }
}
export interface PaymentMethodsResponse {
  error?: boolean
  paymentMethods: PaymentMethodsResponseWithConfiguration | undefined
}

interface PaymentErrorItem {
  code: string
  message: string
}
interface PaymentError {
  errors: PaymentErrorItem[]
}

export interface PaymentMethodParams {
  documentId: string
}

interface FetchPaymentMethodsProps {
  apiBaseUrl: string
  customerContextId: string
  params: PaymentMethodParams
}

const PAYMENT_METHOD_ERROR = {
  error: true,
  paymentMethods: undefined,
}

export const fetchPaymentMethods = async ({
  apiBaseUrl,
  customerContextId,
  params,
}: FetchPaymentMethodsProps): Promise<PaymentMethodsResponse> => {
  try {
    const response = await request<PaymentMethodsResponseWithConfiguration>({
      headers: {
        'Content-Type': 'application/json',
        customerContextId,
      },
      method: 'GET',
      params,
      url: `${apiBaseUrl}/api-payment/paymentMethods`,
    })

    if (response) {
      const { data: paymentMethods } = response

      return {
        paymentMethods,
      }
    } else {
      return PAYMENT_METHOD_ERROR
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
    return PAYMENT_METHOD_ERROR
  }
}

export type PaymentStatus =
  | 'Accepted'
  | 'Pending'
  | 'Rejected'
  | 'Cancelled'
  | 'Error'

type PaymentMethodType = 'scheme' | 'poli' | 'paypal'
interface PaymentAction {
  data: {
    MD: string
    PaReq: string
    TermUrl: string
  }
  method: 'GET' | 'POST'
  paymentData: string
  paymentMethodType: PaymentMethodType
  resendInterval: number
  resendMaxAttempts: number
  sdkData: { token: string }
  type: 'redirect' | 'sdk'
  url: string
}

type ConditionalPaymentResponse = Partial<PaymentResponse> &
  Partial<PaymentError>
export interface PaymentResponse {
  action?: PaymentAction
  documentId: string
  paymentId: string
  pspReference: string
  status: PaymentStatus
}

export interface MakePaymentResponse {
  error?: boolean
  paymentResponse: PaymentResponse | undefined
}
export interface PaymentState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: { browserInfo: any; paymentMethod: any }
}

interface PaymentCompletionDetails {
  MD: string
  PaReq: string
  PaRes: string
  billingToken: string
  'cupsecureplus.smscode': string
  facilitatorAccessToken: string
  oneTimePasscode: string
  orderID: string
  payerID: string
  payload: string
  paymentID: string
  paymentStatus: string
  redirectResult: string
  returnUrlQueryString: string
  'threeds2.challengeResult': string
  'threeds2.fingerprint': string
}
export interface AdditionalPaymentDetails {
  details: Partial<PaymentCompletionDetails>
  paymentData?: string
  paymentId: string
  threeDSAuthenticationOnly?: boolean
}
export interface PaymentParams extends PaymentState {
  customerContextId: string
  documentId: string
  origin: string
  returnUrl: string
}

const PAYMENT_ERROR = {
  error: true,
  paymentResponse: undefined,
}

export const makePayment = async (
  apiBaseUrl: string,
  params: PaymentParams,
): Promise<MakePaymentResponse> => {
  const { customerContextId, data, documentId, origin, returnUrl } = params
  try {
    const response = await request<ConditionalPaymentResponse>({
      data: {
        ...data,
        documentId,
        origin,
        redirectFromIssuerMethod: 'GET',
        returnUrl,
      },
      headers: {
        Accept: 'application/json',
        customerContextId,
      },
      method: 'POST',
      url: `${apiBaseUrl}/api-payment/payments`,
    })

    if (response) {
      const { data: paymentResponse } = response

      return paymentResponse.errors
        ? PAYMENT_ERROR
        : {
            paymentResponse: paymentResponse as PaymentResponse,
          }
    } else {
      return PAYMENT_ERROR
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)

    // Payment API error handling
    // https://digib-brenntag.atlassian.net/browse/BCN-3317
    // There is already a payment available with Success(or Pending) status for this document ID
    if (
      error.response.status === 400 &&
      error.response?.data?.errors[0]?.code === '060.01.500.08'
    ) {
      return {
        error: true,
        paymentResponse: {
          documentId: '',
          paymentId: '',
          pspReference: '',
          status: 'Error',
        },
      }
    }

    return PAYMENT_ERROR
  }
}

type ConditionalPaymentDetailsResponse = Partial<PaymentResponse> &
  Partial<PaymentError>
export interface MakePaymentDetailsResponse {
  error?: boolean
  paymentDetails: PaymentResponse | undefined
}

const PAYMENT_DETAILS_ERROR = {
  error: true,
  paymentDetails: undefined,
}

export const makePaymentDetails = async (
  apiBaseUrl: string,
  data: AdditionalPaymentDetails,
): Promise<MakePaymentDetailsResponse> => {
  try {
    const response = await request<ConditionalPaymentDetailsResponse>({
      data,
      headers: {
        Accept: 'application/json',
      },
      method: 'POST',
      url: `${apiBaseUrl}/api-payment/payments/details`,
    })

    if (response) {
      const { data: paymentDetails } = response

      return paymentDetails.errors
        ? PAYMENT_DETAILS_ERROR
        : {
            paymentDetails: paymentDetails as PaymentResponse,
          }
    } else {
      return PAYMENT_DETAILS_ERROR
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)

    return PAYMENT_DETAILS_ERROR
  }
}
